/**
 * useGetDropMemberLeaderboard — Drop points leaderboard, ranked by balance.
 *
 * Public endpoint: GET /drops/:slug/points/leaderboard (tag DropPoints).
 *
 * Replaces useGetHubMemberLeaderboard, which called the Hub-backed
 * /drops/:slug/members/leaderboard. That route resolved its surface with
 * `HubModel.findOne({slug})`, so it 404'd for every real Drop; this one is
 * Drop-native. It also carried hand-written copies of the response types and an
 * `as unknown as` cast — the generated types are used directly here instead, so
 * schema drift surfaces at compile time rather than at runtime.
 *
 * The old hook took a `sortBy` argument that no caller ever passed; the
 * Drop-native endpoint ranks by balance, which is what every caller relied on.
 */
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { DropPointsApi, DropPointsLeaderboardEntry } from "@devour/client";
import getConfig from "@/utils/getConfig";

export type { DropPointsLeaderboardEntry };

export function useGetDropMemberLeaderboard(slug?: string, offset = 0, limit = 10) {
    return useQuery({
        queryKey: ["drop-member-leaderboard", slug, offset, limit],
        queryFn: () =>
            new DropPointsApi(getConfig(null)).getDropsSlugPointsLeaderboard({
                slug: slug!,
                offset,
                limit,
            }),
        staleTime: 60000,
        enabled: Boolean(slug),
        placeholderData: keepPreviousData,
    });
}
